import { computed, ref, watch, onBeforeUnmount, nextTick, type Ref } from 'vue';

import { isTouchDevice } from '@sg-core/resources/assets/js/utils/media';

import type { PanzoomObject } from '@panzoom/panzoom';

export interface LightboxCursor {
  in?: string;
  out?: string;
}

export interface UsePictureZoomOptions {
  /** Ref to the element that wraps the picture (the pan/zoom container). */
  containerRef: Ref<HTMLElement | null>;
  /** Ref to the element to apply transforms to (typically the <picture> or <img>). */
  elementRef: Ref<HTMLElement | null>;
  /** Whether zoom is currently enabled (e.g. false while lightbox is closing). */
  enabled?: Ref<boolean>;
  /** Scale to zoom to on click. */
  zoomScale?: number;
  /** Number of stepped zoom levels before reset. */
  steps?: number;
  /** Min scale allowed. */
  minScale?: number;
  /** Optional cursor assets for zoom in/out states. */
  cursor?: LightboxCursor;
  /** Callback when zoom state changes. */
  onZoomChange?: (isZoomed: boolean) => void;
}

export interface UsePictureZoomReturn {
  /** Whether the picture is currently zoomed in. */
  isZoomed: Ref<boolean>;
  /** Cursor to use for the current zoom step. */
  currentCursor: Ref<string>;
  /** Toggle zoom on/off at a given point. Ignores drag gestures automatically. */
  toggleZoom: (event?: MouseEvent) => void;
  /** Reset to un-zoomed state (snaps transform immediately). */
  resetZoom: () => void;
  /** Reset only reactive state without touching the panzoom transform. */
  resetZoomState: () => void;
  /** Destroy panzoom instance. */
  destroy: () => void;
  /** Zoom in one step. Does not loop — no-op when already at max step. */
  zoomIn: () => Promise<void>;
  /** Zoom out one step. Does not loop — no-op when already at min step. */
  zoomOut: () => Promise<void>;
  /** Current zoom step (0-based). */
  zoomStep: Ref<number>;
  /** Current scale as a percentage (e.g. 100, 200). */
  scalePercent: Ref<number>;
  /** Whether zoom-in is possible (not at max step). */
  canZoomIn: Ref<boolean>;
  /** Whether zoom-out is possible (not at step 0). */
  canZoomOut: Ref<boolean>;
}

const ZOOM_SCALE_DEFAULT = 2;
const ZOOM_STEPS_DEFAULT = 3;
const MIN_SCALE_DEFAULT = 1;
/** Distance threshold (px) to distinguish a click from a drag. */
const DRAG_THRESHOLD = 5;
const POINTER_MOVE_OPTIONS: AddEventListenerOptions = { passive: false };

/**
 * Composable that manages @panzoom/panzoom for a picture element inside the lightbox.
 *
 * Behaviour:
 *  - Click toggles between 1x (fit) and zoomScale (zoomed).
 *  - When zoomed, panning is enabled via drag; drags do not trigger zoom toggle.
 *  - Mouse wheel zooms in/out at the pointer focal point.
 *  - Escape / slide-change should call `resetZoom()` externally.
 *  - Touch pinch-zoom is handled natively by panzoom.
 */
export function usePictureZoom(options: UsePictureZoomOptions): UsePictureZoomReturn {
  const {
    containerRef,
    elementRef,
    enabled,
    zoomScale = ZOOM_SCALE_DEFAULT,
    steps = ZOOM_STEPS_DEFAULT,
    minScale = MIN_SCALE_DEFAULT,
    cursor,
    onZoomChange,
  } = options;

  const isZoomed = ref(false);
  const zoomStep = ref(0);
  const currentScale = ref(minScale);
  let panzoomInstance: PanzoomObject | null = null;
  let boundContainer: HTMLElement | null = null;
  let boundElement: HTMLElement | null = null;

  const scalePercent = computed(() => Math.round(currentScale.value * 100));
  const canZoomIn = computed(() => zoomStep.value < getScales().length - 1);
  const canZoomOut = computed(() => zoomStep.value > 0);

  const currentCursor = computed(() => {
    if (isTouchDevice()) {
      return 'auto';
    }

    const atMax = zoomStep.value >= getScales().length - 1;

    if (!cursor?.in || !cursor?.out) {
      return atMax ? 'zoom-out' : 'zoom-in';
    }

    const icon = atMax ? cursor.out : cursor.in;
    return `url("${icon}") 25 25, pointer`;
  });

  // --- Drag detection state ---
  // Track pointer-down position so we can distinguish a click from a drag.
  let pointerDownX = 0;
  let pointerDownY = 0;
  let activePointerId: number | null = null;
  let wasDrag = false;
  const touchPointerIds = new Set<number>();

  function handlePointerDown(event: PointerEvent): void {
    if (event.pointerType === 'touch') {
      touchPointerIds.add(event.pointerId);
    }

    if (activePointerId === null) {
      activePointerId = event.pointerId;
      pointerDownX = event.clientX;
      pointerDownY = event.clientY;
      wasDrag = false;
    }

    if (panzoomInstance) {
      panzoomInstance.handleDown(event);
      return;
    }

    void ensureInstance().then((instance) => {
      if (!instance || enabled?.value === false || !isTrackedPointer(event.pointerId)) {
        return;
      }

      instance.handleDown(event);
    });
  }

  function handlePointerMove(event: PointerEvent): void {
    if (activePointerId === null && touchPointerIds.size === 0) {
      return;
    }

    if (event.pointerId === activePointerId && !wasDrag) {
      const dx = Math.abs(event.clientX - pointerDownX);
      const dy = Math.abs(event.clientY - pointerDownY);

      if (dx > DRAG_THRESHOLD || dy > DRAG_THRESHOLD) {
        wasDrag = true;
      }
    }

    if (!panzoomInstance) {
      return;
    }

    if (!isTrackedPointer(event.pointerId)) {
      return;
    }

    const isAtMinScale = panzoomInstance.getScale() <= minScale + 0.05;
    const isTouchPointer = event.pointerType === 'touch';
    const isPinchGesture = isTouchPointer && touchPointerIds.size > 1;

    panzoomInstance.setOptions({ disablePan: isAtMinScale });

    if (isTouchPointer && isAtMinScale && !isPinchGesture) {
      return;
    }

    if (isTouchPointer) {
      event.stopPropagation();

      if (event.cancelable) {
        event.preventDefault();
      }
    }

    panzoomInstance.handleMove(event);
  }

  function handlePointerUp(event: PointerEvent): void {
    if (panzoomInstance && isTrackedPointer(event.pointerId)) {
      panzoomInstance.handleUp(event);
    }

    if (event.pointerType === 'touch') {
      touchPointerIds.delete(event.pointerId);
    }

    if (event.pointerId === activePointerId) {
      activePointerId = null;
    }
  }

  function isTrackedPointer(pointerId: number): boolean {
    return activePointerId === pointerId || touchPointerIds.has(pointerId);
  }

  function getImageElement(): HTMLImageElement | null {
    return elementRef.value?.querySelector('img') ?? null;
  }

  function getContainerDimensions() {
    const rect = containerRef.value?.getBoundingClientRect();

    return {
      width: rect?.width ?? 0,
      height: rect?.height ?? 0,
    };
  }

  function getMaxScale(): number {
    const image = getImageElement();

    if (!image) {
      return Math.max(zoomScale, minScale);
    }

    const { naturalWidth, naturalHeight } = image;

    if (!naturalWidth || !naturalHeight) {
      return Math.max(zoomScale, minScale);
    }

    const { width: containerWidth, height: containerHeight } = getContainerDimensions();

    if (!containerWidth || !containerHeight) {
      return Math.max(zoomScale, minScale);
    }

    const dominantScale =
      naturalWidth > naturalHeight ? naturalWidth / containerWidth : naturalHeight / containerHeight;

    return Math.max(dominantScale, minScale);
  }

  function getScales(): number[] {
    const stepCount = Math.max(steps, 1);
    const computedMaxScale = Math.max(getMaxScale(), minScale);

    if (stepCount === 1 || computedMaxScale <= minScale + 0.01) {
      return [minScale];
    }

    if (stepCount === 2) {
      return [minScale, computedMaxScale];
    }

    const increment = (computedMaxScale - minScale) / (stepCount - 1);
    const scales = [];

    for (let index = 0; index < stepCount; index += 1) {
      scales.push(minScale + increment * index);
    }

    return scales;
  }

  function syncZoomState(scale: number): void {
    currentScale.value = scale;
    panzoomInstance?.setOptions({ disablePan: scale <= minScale + 0.05 });
    setZoomedState(scale > minScale + 0.05);

    if (scale <= minScale + 0.05) {
      zoomStep.value = 0;
      return;
    }

    const scales = getScales();
    const nextStep = scales.findLastIndex((entry) => scale >= entry - 0.05);
    zoomStep.value = nextStep > 0 ? Math.min(nextStep, scales.length - 1) : 1;
  }

  /**
   * Lazily import and create the panzoom instance.
   * We use dynamic import because panzoom is a relatively heavy dependency
   * that should only load when zoom is actually used.
   */
  async function ensureInstance(): Promise<PanzoomObject | null> {
    if (panzoomInstance) {
      return panzoomInstance;
    }

    const el = elementRef.value;

    if (!el) {
      return null;
    }

    const { default: Panzoom } = await import('@panzoom/panzoom');

    panzoomInstance = Panzoom(el, {
      maxScale: getMaxScale(),
      minScale,
      startScale: minScale,
      cursor: currentCursor.value,
      disablePan: true,
      noBind: true,
      panOnlyWhenZoomed: true,
      touchAction: 'none',
      animate: true,
      duration: 450,
      easing: 'cubic-bezier(0.4, 0, 0.2, 1)',
      // Exclude buttons/links from pan handling
      excludeClass: 'phxsg-lightbox__zoom-exclude',
      handleStartEvent: (event: Event) => {
        // Only prevent default when zoomed (to allow drag panning).
        // When not zoomed, let the click bubble for toggle.
        if (isZoomed.value) {
          event.preventDefault();
        }
      },
    });

    // Panzoom writes its identity transform inline during construction. If we
    // call `zoom()` in the same microtask the browser coalesces both writes
    // into a single paint and the first zoom-in has no starting matrix to
    // interpolate from — it snaps instead of animating. Wait one frame so the
    // identity transform is committed first.
    await new Promise<void>((resolve) => requestAnimationFrame(() => resolve()));

    return panzoomInstance;
  }

  /**
   * Sync panzoom's maxScale with the current image/container dimensions.
   * Needed because the instance may have been created before the image loaded
   * (warmup), so its initial maxScale could be stale.
   */
  function syncPanzoomMaxScale(): void {
    panzoomInstance?.setOptions({ maxScale: getMaxScale() });
  }

  function setZoomedState(zoomed: boolean): void {
    if (isZoomed.value !== zoomed) {
      isZoomed.value = zoomed;
      onZoomChange?.(zoomed);
    }
  }

  /**
   * Compute the `focal` option for `panzoom.zoom()` such that the given
   * client-space point stays anchored while the scale changes — i.e. the
   * same math panzoom's `zoomToPoint` runs internally, but callable with
   * the animated `zoom()` API.
   *
   * We reimplement this because panzoom's `zoomToPoint` internally forces
   * `animate: false` regardless of the option passed in (see panzoom source
   * line 648 `zoom(... { animate: false, focal })`), so using it means the
   * first zoom always snaps. Going through `zoom()` preserves animation.
   */
  function computeFocal(event: MouseEvent, toScale: number, currentScale: number): { x: number; y: number } | null {
    const el = elementRef.value;
    const parent = el?.parentElement;

    if (!el || !parent) {
      return null;
    }

    const parentRect = parent.getBoundingClientRect();
    const parentStyle = window.getComputedStyle(parent);
    const elemRect = el.getBoundingClientRect();

    const padL = parseFloat(parentStyle.paddingLeft) || 0;
    const padT = parseFloat(parentStyle.paddingTop) || 0;
    const bdL = parseFloat(parentStyle.borderLeftWidth) || 0;
    const bdT = parseFloat(parentStyle.borderTopWidth) || 0;
    const mgL = parseFloat(window.getComputedStyle(el).marginLeft) || 0;
    const mgT = parseFloat(window.getComputedStyle(el).marginTop) || 0;

    let clientX = event.clientX - parentRect.left - padL - bdL - mgL;
    let clientY = event.clientY - parentRect.top - padT - bdT - mgT;

    // HTML elements have transform-origin 50% 50%; offset by element center
    // in pre-scale (natural) coordinates, as panzoom's zoomToPoint does.
    clientX -= elemRect.width / currentScale / 2;
    clientY -= elemRect.height / currentScale / 2;

    return {
      x: clientX * toScale,
      y: clientY * toScale,
    };
  }

  /**
   * Toggle between fit (1x) and zoomed state at the click point.
   * Automatically ignores clicks that were actually drags (pan gestures).
   */
  async function toggleZoom(event?: MouseEvent): Promise<void> {
    if (isTouchDevice()) {
      return;
    }

    // If the pointer moved significantly between down and up, treat as drag — skip toggle.
    if (wasDrag) {
      return;
    }

    const instance = await ensureInstance();

    if (!instance) {
      return;
    }

    syncPanzoomMaxScale();

    const scales = getScales();

    if (zoomStep.value < scales.length - 1) {
      zoomStep.value += 1;

      const nextScale = scales[zoomStep.value] ?? zoomScale;

      if (event) {
        const focal = computeFocal(event, nextScale, instance.getScale());
        // `zoom` honours `animate: true` (from constructor defaults); unlike
        // `zoomToPoint`, which force-overrides it to false.
        instance.zoom(nextScale, focal ? { animate: true, focal } : { animate: true });
      } else {
        instance.zoom(nextScale, { animate: true });
      }

      syncZoomState(nextScale);
    } else {
      instance.reset({ animate: true });
      syncZoomState(minScale);
    }
  }

  /**
   * Reset to un-zoomed state (1x). Snaps the panzoom transform back immediately.
   * Use on close or when the element is about to be replaced.
   */
  function resetZoom(): void {
    if (panzoomInstance) {
      panzoomInstance.reset({ animate: false });
    }
    syncZoomState(minScale);
  }

  /**
   * Reset only the reactive zoom state (step, scale, isZoomed) without
   * touching the panzoom transform. Use on navigation so the outgoing item
   * keeps its zoomed scale during the leave transition.
   */
  function resetZoomState(): void {
    syncZoomState(minScale);
  }

  /**
   * Zoom in one step. Unlike `toggleZoom`, this does not check for
   * drag gestures or touch devices and does not loop back to 1x at max.
   * Designed for explicit UI controls (buttons).
   */
  async function zoomIn(): Promise<void> {
    const instance = await ensureInstance();

    if (!instance) {
      return;
    }

    syncPanzoomMaxScale();

    const scales = getScales();

    if (zoomStep.value >= scales.length - 1) {
      return;
    }

    zoomStep.value += 1;
    const nextScale = scales[zoomStep.value] ?? zoomScale;
    instance.zoom(nextScale, { animate: true });
    syncZoomState(nextScale);
  }

  /**
   * Zoom out one step. No-op when already at step 0 (1x).
   * Designed for explicit UI controls (buttons).
   */
  async function zoomOut(): Promise<void> {
    const instance = await ensureInstance();

    if (!instance) {
      return;
    }

    syncPanzoomMaxScale();

    if (zoomStep.value <= 0) {
      return;
    }

    zoomStep.value -= 1;

    if (zoomStep.value === 0) {
      instance.reset({ animate: true });
      syncZoomState(minScale);
    } else {
      const scales = getScales();
      const prevScale = scales[zoomStep.value] ?? minScale;
      instance.zoom(prevScale, { animate: true });
      syncZoomState(prevScale);
    }
  }

  /**
   * Clean up the panzoom instance.
   */
  function destroy(): void {
    if (panzoomInstance) {
      panzoomInstance.destroy();
      panzoomInstance = null;
    }
    setZoomedState(false);
  }

  /**
   * Listen to panzoom's own events to keep isZoomed in sync
   * (e.g. after pinch-zoom on touch devices).
   */
  function handlePanzoomEnd(): void {
    if (!panzoomInstance) {
      return;
    }

    syncZoomState(panzoomInstance.getScale());
  }

  // --- Listener management ---

  /**
   * Reset zoom and destroy the panzoom instance on viewport resize.
   * Container dimensions change, making the current transform scale stale.
   * Destroying forces a fresh instance with the updated maxScale on the next
   * zoom interaction.
   */
  function handleResize(): void {
    if (!isZoomed.value) {
      return;
    }

    resetZoom();
    destroy();
  }

  function bindListeners(): void {
    const container = containerRef.value;
    const el = elementRef.value;

    if (!container || !el) {
      return;
    }

    unbindListeners();

    boundContainer = container;
    boundElement = el;

    boundContainer.addEventListener('pointerdown', handlePointerDown);
    document.addEventListener('pointermove', handlePointerMove, POINTER_MOVE_OPTIONS);
    document.addEventListener('pointerup', handlePointerUp);
    document.addEventListener('pointercancel', handlePointerUp);
    el.addEventListener('panzoomend', handlePanzoomEnd);
    window.addEventListener('resize', handleResize);
  }

  function unbindListeners(): void {
    boundContainer?.removeEventListener('pointerdown', handlePointerDown);
    document.removeEventListener('pointermove', handlePointerMove, POINTER_MOVE_OPTIONS);
    document.removeEventListener('pointerup', handlePointerUp);
    document.removeEventListener('pointercancel', handlePointerUp);
    boundElement?.removeEventListener('panzoomend', handlePanzoomEnd);
    window.removeEventListener('resize', handleResize);
    boundContainer = null;
    boundElement = null;
    activePointerId = null;
    wasDrag = false;
    touchPointerIds.clear();
  }

  watch(currentCursor, (value) => {
    panzoomInstance?.setOptions({ cursor: value, animate: true });
  });

  watch(
    [containerRef, elementRef],
    async () => {
      unbindListeners();
      destroy();

      if (enabled?.value) {
        await nextTick();
        bindListeners();
      }
    },
    { flush: 'post' },
  );

  // Watch the enabled ref to init/destroy as needed.
  // We eagerly create the panzoom instance here rather than lazily on the
  // first click. Reason: panzoom writes its identity transform inside a RAF
  // during construction, and if the user's first click also lands in that same
  // RAF the browser coalesces both writes and the first zoom-in snaps instead
  // of animating. Creating the instance ahead of time guarantees the identity
  // transform has been committed and painted before any animated zoom call.
  if (enabled) {
    watch(
      enabled,
      async (val) => {
        if (val) {
          await nextTick();
          unbindListeners();
          bindListeners();
          // Fire-and-forget: warm up the instance so the first click animates.
          void ensureInstance();
        } else {
          // Intentionally do not call `resetZoom()` here: when the lightbox
          // is closing, resetting the transform would snap the picture back
          // to its initial scale while the overlay is still fading out.
          // `destroy()` only removes panzoom's listeners; the inline transform
          // is left untouched on the element so the exit animation stays
          // consistent with the user's last zoom state.
          unbindListeners();
          destroy();
        }
      },
      { immediate: true },
    );
  }

  onBeforeUnmount(() => {
    unbindListeners();
    destroy();
  });

  return {
    isZoomed,
    currentCursor,
    toggleZoom,
    resetZoom,
    resetZoomState,
    destroy,
    zoomIn,
    zoomOut,
    zoomStep,
    scalePercent,
    canZoomIn,
    canZoomOut,
  };
}
